<template>
  <div
    class="tw-min-h-screen tw-w-full tw-fixed tw-top-0 tw-left-0 tw-z-20 tw-bg-white dark:tw-bg-dm"
  >
    <div
      v-if="foreigner"
      class="tw-flex tw-h-full"
    >
      <aside class="tw-relative tw-w-80 tw-border-r tw-border-gray dark:tw-border-dm-gray tw-flex-shrink-0">
        <header class="tw-h-16 tw-flex tw-p-3 tw-border-b tw-border-gray dark:tw-border-dm-gray">
          <ForeignerFamilyPopoverList
            :family="foreigner.family"
            :foreigner-id="foreigner?.id"
            show-messages-count
            :redirect-on-family-member-click="false"
            class="tw-mr-2.5 tw-leading-[0px]"
            :link="(id) => `/foreigners/${id}/messaging`"
            store-path="v2/admin/messaging"
            @family-member-click="handleFamilyMemberChange"
          >
            <i-user-avatar
              :avatar="foreigner.avatar_url && foreigner.avatar_url['128'] && `${$baseHost}/${foreigner.avatar_url['128']}`"
              :fullname="`${foreigner.first_name} ${foreigner.last_name}`"
              size="mini"
            />
          </ForeignerFamilyPopoverList>
          <ForeignersMessagingFulltextSearch />
        </header>
        <div
          ref="ForeignersMessagingThreads"
          class="ForeignersMessagingThreads tw-overflow-auto"
          :class="openedThreadData.id === 'all' || fulltextSearchValue.length ? 'tw-divide-y tw-divide-gray dark:tw-divide-dm-gray tw-px-4' : 'tw-px-2'"
          @scroll="handleScroll"
        >
          <template v-if="fulltextSearchValue.length">
            <ForeignersMessagingThread
              v-for="message in $store.state.v2.admin.messaging.fulltextSearchResults"
              :key="message.id"
              :thread="message"
              :is-active="message.id === $store.state.v2.admin.messaging.fulltextMessageId"
              @click.native="() => {
                $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(message.permit_id), foreignerId: message.foreigner_id}]);
                $store.commit('v2/admin/messaging/set', ['fulltextMessageId', message.id]);
              }"
            />
          </template>

          <template v-else>
            <div
              v-if="threads.length === 0"
              class="tw-flex tw-h-full tw-justify-center tw-items-center"
            >
              {{ $t('messaging.no_threads') }}
            </div>
            <ForeignersMessagingThread
              v-for="thread in threads"
              :key="thread.id"
              :thread="thread"
              :is-active="String(thread.permit_id) === String(openedThreadData.id)"
              @click.native="() => {
                $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(thread.permit_id), foreignerId: thread.foreigner_id }]);
                if ($route.params.mid) {
                  $router.replace({
                    name: 'foreignersMessagingPID',
                    params: { fid: $route.params.fid, pid: String(thread.permit_id), mid: null }
                  });
                }
              }"
            />
          </template>
          <div
            v-if="moreThreadsUrl"
            v-loading="true"
            class="tw-w-full tw-h-14"
          />
        </div>
        <i-button
          class="tw-fixed tw-bottom-[74px] tw-left-[256px] tw-shadow tw-border-none"
          is-icon
          type="white"
          @click="isNewThreadDialogOpen = true"
        >
          <icon-plus />
        </i-button>
        <div class="tw-m-3">
          <i-button
            class="tw-w-full"
            @click="() => {
              $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: 'all', foreignerId: openedThreadData.foreignerId }]);
              $store.commit('v2/admin/messaging/set', ['fulltextSearchValue', '']);
              $store.commit('v2/admin/messaging/set', ['fulltextMessageId', null]);
            }"
          >
            <span class="tw-font-bold">
              {{ $t('messaging.all_threads') }}
            </span>
          </i-button>
        </div>
      </aside>
      <div class="tw-flex-1">
        <header class="tw-h-16 tw-border-b tw-border-gray dark:tw-border-dm-gray tw-relative">
          <ForeignersMessagingFilterSearch
            v-if="isSearchOpen"
            class="tw-px-8 tw-gap-2"
            show-close
            @close="isSearchOpen = false"
          />
          <div
            v-else-if="isFiltersOpen"
            class="tw-flex tw-h-full tw-justify-center tw-items-center tw-px-8"
          >
            <ForeignersMessagingFilters
              class="tw-flex-1"
              is-inline
            />
            <i-button
              size="medium"
              is-icon
              class="tw-ml-6"
              @click="isFiltersOpen = false"
            >
              <icon-close />
            </i-button>
          </div>
          <div
            v-else
            class="tw-flex tw-h-full tw-justify-center tw-items-center"
          >
            <div class="tw-font-bold">
              {{ permit ? permit.subject : $t('messaging.all_threads') }}
            </div>
            <div class="tw-absolute tw-top-3 tw-right-8 tw-flex tw-gap-3 tw-items-center">
              <i-button
                size="medium"
                is-icon
                @click="isSearchOpen = true"
              >
                <icon-search />
              </i-button>
              <i-button
                size="medium"
                is-icon
                @click="isFiltersOpen = true"
              >
                <icon-filters />
              </i-button>
              <router-link
                :to="`/foreigners/${foreigner.id}`"
              >
                <i-button
                  size="medium"
                  is-icon
                  @click="isFiltersOpen = false; isSearchOpen = false"
                >
                  <icon-close />
                </i-button>
              </router-link>
            </div>
          </div>
        </header>
        <div class="ForeignersMessagingMessages tw-bg-light dark:tw-bg-transparent tw-bg-opacity-40">
          <ForeignersMessagingMessages
            v-if="openedThreadData.id"
            :permit-id="openedThreadData.id"
            class="tw-flex tw-flex-col tw-items-center"
            :show-permit="openedThreadData.id === 'all'"
            :show-send-message="openedThreadData.id !== 'all'"
            :foreigner-id="foreigner.id"
            is-fullscreen
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="tw-flex tw-h-full"
    >
      <aside class="tw-relative tw-w-80 tw-border-r tw-border-gray dark:tw-border-dm-gray tw-flex-shrink-0">
        <header class="tw-h-16 tw-flex tw-p-3 tw-border-b tw-border-gray dark:tw-border-dm-gray">
          <ForeignersMessagingFulltextSearch />
        </header>
        <div
          ref="ForeignersMessagingThreads"
          class="ForeignersMessagingThreads tw-overflow-auto"
          :class="[
            openedThreadData.id === 'all' || fulltextSearchValue.length ? 'tw-divide-y tw-divide-gray dark:tw-divide-dm-gray tw-px-4' : 'tw-px-2',
            foreigner ? '' : 'full-height',
          ]"
          @scroll="handleScroll"
        >
          <template v-if="fulltextSearchValue.length">
            <ForeignersMessagingThread
              v-for="message in $store.state.v2.admin.messaging.fulltextSearchResults"
              :key="message.id"
              :thread="message"
              :is-active="message.id === $store.state.v2.admin.messaging.fulltextMessageId"
              @click.native="() => {
                $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(message.permit_id), foreignerId: message.foreigner_id }]);
                $store.commit('v2/admin/messaging/set', ['fulltextMessageId', message.id]);
              }"
            />
          </template>

          <template v-else>
            <div
              v-if="threads.length === 0"
              class="tw-flex tw-h-full tw-justify-center tw-items-center"
            >
              {{ $t('messaging.no_threads') }}
            </div>
            <ForeignersMessagingThread
              v-for="thread in threads"
              :key="thread.id"
              :thread="thread"
              :is-active="String(thread.id) === String(openedThread?.id)"
              @click.native="() => {
                $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: String(thread.permit_id), foreignerId: thread.foreigner_id }]);
                $route.params.mid && $router.replace({ params: { pid: String(thread.permit_id), mid: null } });
              }"
            />
          </template>
          <div
            v-if="moreThreadsUrl"
            v-loading="true"
            class="tw-w-full tw-h-14"
          />
        </div>
        <i-button
          v-if="foreigner"
          class="tw-fixed tw-bottom-[74px] tw-left-[256px] tw-shadow tw-border-none"
          is-icon
          type="white"
          @click="isNewThreadDialogOpen = true"
        >
          <icon-plus />
        </i-button>
        <div
          v-if="foreigner"
          class="tw-m-3"
        >
          <i-button
            class="tw-w-full"
            @click="() => {
              $store.commit('v2/admin/messaging/set', ['openedThreadData', { id: 'all', foreignerId: openedThreadData.foreignerId }]);
              $store.commit('v2/admin/messaging/set', ['fulltextSearchValue', '']);
              $store.commit('v2/admin/messaging/set', ['fulltextMessageId', null]);
            }"
          >
            <span class="tw-font-bold">
              {{ $t('messaging.all_threads') }}
            </span>
          </i-button>
        </div>
      </aside>
      <div class="tw-flex-1">
        <header class="tw-h-16 tw-border-b tw-border-gray dark:tw-border-dm-gray tw-relative">
          <ForeignersMessagingFilterSearch
            v-if="isSearchOpen"
            class="tw-px-8 tw-gap-2"
            show-close
            @close="isSearchOpen = false"
          />
          <div
            v-else-if="isFiltersOpen"
            class="tw-flex tw-h-full tw-justify-center tw-items-center tw-px-8"
          >
            <ForeignersMessagingFilters
              class="tw-flex-1"
              is-inline
            />
            <i-button
              size="medium"
              is-icon
              class="tw-ml-6"
              @click="isFiltersOpen = false"
            >
              <icon-close />
            </i-button>
          </div>
          <div
            v-else
            class="tw-flex tw-h-full tw-justify-center tw-items-center"
          >
            <div class="tw-font-bold">
              {{ permit ? permit.subject : $t('messaging.all_threads') }}
            </div>
            <div class="tw-absolute tw-top-3 tw-right-8 tw-flex tw-gap-3 tw-items-center">
              <i-button
                size="medium"
                is-icon
                @click="isSearchOpen = true"
              >
                <icon-search />
              </i-button>
              <i-button
                size="medium"
                is-icon
                @click="isFiltersOpen = true"
              >
                <icon-filters />
              </i-button>
              <router-link
                v-if="foreigner"
                :to="`/foreigners/${foreigner?.id}`"
              >
                <i-button
                  size="medium"
                  is-icon
                  @click="isFiltersOpen = false; isSearchOpen = false"
                >
                  <icon-close />
                </i-button>
              </router-link>
              <i-button
                size="medium"
                is-icon
                @click="$router.back()"
              >
                <icon-close />
              </i-button>
            </div>
          </div>
        </header>
        <div
          v-if="openedThreadData.id"
          class="ForeignersMessagingMessages full-screen tw-bg-light dark:tw-bg-transparent tw-bg-opacity-40"
        >
          <ForeignersMessagingMessages
            v-if="openedThreadData.id"
            :permit-id="openedThreadData.id"
            class="tw-flex tw-flex-col tw-items"
            :show-permit="openedThreadData.id === 'all'"
            :show-send-message="openedThreadData.id !== 'all'"
            :foreigner-id="openedThread?.foreigner_id"
            is-fullscreen
          />
        </div>
        <div
          v-else
          class="tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full"
        >
          <h3 class="tw-text-gray-dark">
            {{ $t('messaging.no_thread_selected') }}
          </h3>
        </div>
      </div>
    </div>

    <ForeignersNewThreadDialog
      v-if="foreigner"
      :is-open="isNewThreadDialogOpen"
      :can-be-opened-threads="canBeOpenedThreads"
      store-path="v2/admin/messaging"
      @close="isNewThreadDialogOpen = false"
    />
  </div>
</template>

<script>
import ForeignerFamilyPopoverList from '@/components/Foreigner/ForeignerFamilyPopoverList.vue';
import ForeignersMessagingFilters from '@/components/Foreigners/Messaging/ForeignersMessagingFilters.vue';
import ForeignersMessagingMessages from '@/components/Foreigners/Messaging/ForeignersMessagingMessages.vue';
import ForeignersMessagingThread from '@/components/Foreigners/Messaging/ForeignersMessagingThread.vue';
import ForeignersMessagingFilterSearch from '@/components/Foreigners/Messaging/ForeignersMessagingFilterSearch.vue';
import ForeignersMessagingFulltextSearch from '@/components/Foreigners/Messaging/ForeignersMessagingFulltextSearch.vue';
import ForeignersNewThreadDialog from '@/components/Foreigners/Messaging/ForeignersNewThreadDialog.vue';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'ForeignersMessaging',
  components: {
    ForeignersNewThreadDialog,
    ForeignerFamilyPopoverList,
    ForeignersMessagingFilters,
    ForeignersMessagingMessages,
    ForeignersMessagingThread,
    ForeignersMessagingFilterSearch,
    ForeignersMessagingFulltextSearch,
  },

  data() {
    return {
      isSearchOpen: false,
      isFiltersOpen: false,
      timerInterval: null,
      isNewThreadDialogOpen: false,
      isMoreThreadsLoading: false,
    };
  },

  computed: {
    ...mapState({
      foreigner: (state) => state.v2.admin.messaging.foreigner,
      openedThreadData: (state) => state.v2.admin.messaging.openedThreadData,
      moreThreadsUrl: (state) => state.v2.admin.messaging.moreThreadsUrl,
      fulltextSearchValue: (state) => state.v2.admin.messaging.fulltextSearchValue,
    }),
    ...mapGetters({
      threads: 'v2/admin/messaging/threads',
      canBeOpenedThreads: 'v2/admin/messaging/canBeOpenedThreads',
      openedThread: 'v2/admin/messaging/getOpenedThreadPermit',
      permit: 'v2/admin/messaging/getOpenedThreadPermit',
    }),
    permit() {
      return this.threads.find(({ permit_id }) => String(permit_id) === String(this.openedThreadData.id));
    },
  },

  watch: {
    'openedThread.foreigner_id'(newThreadForeignerId) {
      if (newThreadForeignerId) {
        this.$store.dispatch('v2/admin/messaging/loadMessagingUsers', { foreignerId: newThreadForeignerId });
      }
    },
  },

  async mounted() {
    this.$store.commit('v2/admin/foreigners/foreigner/set', ['foreigner', null]);
    if (this.$route.params.fid !== 'preview') {
      this.$store.dispatch('v2/admin/messaging/loadForeignerPermits', this.$route.params.fid);
      await this.$store.dispatch('v2/admin/messaging/loadForeignerDetail', this.$route.params.fid); // TODO: duplicate foreigner fetch, get rid of v2/admin/foreigners/foreigner/loadForeignerDetail call
      await this.$store.dispatch('v2/admin/foreigners/foreigner/loadForeignerDetail', this.$route.params.fid);
      this.$store.dispatch('v2/admin/messaging/loadMessagingUsers', { foreignerId: this.$route.params.fid });
    }

    if (this.$route.params.pid) {
      this.$store.commit('v2/admin/messaging/set', ['openedThreadData', { id: this.$route.params.pid, foreignerId: this.$route.params.fid }]);
    } else if (!this.openedThreadData.id) {
      if (this.$route.params.fid !== 'preview') {
        this.$store.commit('v2/admin/messaging/set', ['openedThreadData', { id: 'all', foreignerId: this.$route.params.fid }]);
      } else {
        this.$store.commit('v2/admin/messaging/set', ['openedThreadData', { id: null, foreignerId: null }]);
      }
    }

    this.loadThreads();
    this.timerInterval = setInterval(() => {
      this.loadThreads();
    }, 30 * 1000);
  },
  beforeUnmount() {
    clearTimeout(this.timerInterval);
  },

  methods: {
    async loadThreads() {
      await this.$store.dispatch('v2/admin/messaging/loadThreads', this.foreigner?.id);
    },
    async loadMoreThreads() {
      this.isMoreThreadsLoading = true;
      await this.$store.dispatch('v2/admin/messaging/loadMoreThreads');
      this.isMoreThreadsLoading = false;
    },
    handleScroll() {
      const container = this.$refs.ForeignersMessagingThreads;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight; // Adjust for visible height

      if (this.moreThreadsUrl && !this.isMoreThreadsLoading && distanceToBottom < 10) {
        this.loadMoreThreads();
      }
    },
    async handleFamilyMemberChange(foreignerId) {
      if (foreignerId) {
        await this.$store.dispatch('v2/admin/messaging/loadForeignerDetail', foreignerId);
        this.$store.dispatch('v2/admin/messaging/loadForeignerPermits', foreignerId);
      }
      await this.loadThreads();
    },
  },
};
</script>

<style lang="scss" scoped>
.ForeignersMessagingThreads {
  height: calc(100vh - 4rem - 4.5rem);
  overscroll-behavior: none;

  &.full-height {
    height: calc(100vh - 4rem);
  }
}

.ForeignersMessagingMessages.full-screen {
  height: calc(100vh - 4rem);
}
</style>
